import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Avatar,
  CircularProgress,
  Tab,
  Chip,
} from '@mui/material';
import {
  School as SchoolIcon,
  Quiz as QuizIcon,
  Lock as LockIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { testsAPI, usersAPI } from '../services/api';
import Button from '../components/UIKit/Button';
import GradientProgress from '../components/UIKit/GradientProgress';
import Tabs from '../components/UIKit/Tabs';
import AchievementCard from '../components/AchievementCard';
import CourseCard from '../components/CourseCard';
import TestResultCard from '../components/TestResultCard';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// Компонент отображения публичного профиля пользователя
const UserProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(['profile', 'common']);
  const { user: currentUser, isAuthenticated } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loadingCourses, setLoadingCourses] = useState(false);
  const [loadingAchievements, setLoadingAchievements] = useState(false);
  const [loadingTestResults, setLoadingTestResults] = useState(false);
  const [userData, setUserData] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [courses, setCourses] = useState({
    inProgressCourses: [],
    notStartedCourses: [],
    completedCourses: [],
  });
  const [achievements, setAchievements] = useState([]);
  const [testResults, setTestResults] = useState({
    results: [],
    pagination: { totalItems: 0 },
  });
  const [error, setError] = useState(null);
  const [hasPrivateAccess, setHasPrivateAccess] = useState(false);

  // URL для изображений
  const serverUrl = process.env.REACT_APP_APP_URL || '';

  // Получаем курсы пользователя
  const fetchUserCourses = useCallback(async () => {
    try {
      setLoadingCourses(true);
      const response = await usersAPI.getUserCourses(userId);
      const coursesData = response.data.courses || response.data;
      setCourses({
        inProgressCourses: coursesData.inProgressCourses || [],
        notStartedCourses: coursesData.notStartedCourses || [],
        completedCourses: coursesData.completedCourses || [],
      });
    } catch (err) {
      console.error(t('profile:error_loading_courses'), err);
    } finally {
      setLoadingCourses(false);
    }
  }, [userId, t]);

  // Получаем результаты тестов пользователя
  const fetchUserTestResults = useCallback(async () => {
    try {
      setLoadingTestResults(true);
      const response = await usersAPI.getUserTestResults(userId);
      setTestResults(response.data);
    } catch (err) {
      console.error(
        t('profile:error_loading_test_results'),
        err
      );
    } finally {
      setLoadingTestResults(false);
    }
  }, [userId, t]);

  // Получаем основную информацию о профиле пользователя
  const fetchUserProfile = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await usersAPI.getUserProfile(userId);
      setUserData(response.data);

      // Проверяем, есть ли доступ к приватным данным
      const hasAccess =
        currentUser &&
        (currentUser.roles.includes('admin') ||
          currentUser._id === userId ||
          response.data.achievements !== undefined); // Если получили достижения, значит профиль открыт

      setHasPrivateAccess(hasAccess);

      // Для admin/teacher — загружаем всё, для студента — только достижения
      if (hasAccess) {
        if (currentUser.roles.includes('admin') || currentUser.roles.includes('teacher')) {
          setLoadingAchievements(true);
          fetchUserCourses();
          fetchUserTestResults();
          if (response.data.achievements) {
            setAchievements(response.data.achievements);
          }
          setLoadingAchievements(false);
        } else if (currentUser.roles.includes('student')) {
          setLoadingAchievements(true);
          if (response.data.achievements) {
            setAchievements(response.data.achievements);
          }
          setLoadingAchievements(false);
        }
      }

      setLoading(false);
    } catch (err) {
      console.error(t('profile:profile_load_error'), err);
      setError(t('profile:profile_load_error'));
      setLoading(false);
      setLoadingAchievements(false);
      setLoadingCourses(false);
      setLoadingTestResults(false);
    }
  }, [userId, currentUser, t, fetchUserCourses, fetchUserTestResults]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    fetchUserProfile();
  }, [userId, isAuthenticated, fetchUserProfile, navigate]);

  // Обработчик изменения вкладки
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Если данные загружаются, показываем индикатор загрузки
  if (loading) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="300px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Если произошла ошибка, показываем сообщение об ошибке
  if (error) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" color="error">
            {error}
          </Typography>
          <Button
            color="primary"
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate(-1)}
          >
            {t('common:actions.back')}
          </Button>
        </Box>
      </Container>
    );
  }

  // Если пользователь не найден, показываем сообщение
  if (!userData) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6">{t('profile:user_not_found')}</Typography>
          <Button
            color="primary"
            variant="contained"
            sx={{ mt: 2 }}
            onClick={() => navigate(-1)}
          >
            {t('common:actions.back')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        {/* Заголовок страницы */}
        <Typography component="h1" variant="h3" sx={{ mb: 4 }}>
          {t('profile:user_profile')}
        </Typography>

        {/* Общая информация о пользователе */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: '12px',
            mb: 4,
          }}
        >
          {/* FLEX-контейнер: інформація ліворуч, аватар праворуч (або знизу на мобільних) */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'center', md: 'flex-start' },
              gap: 3,
            }}
          >
            {/* Аватар ліворуч */}
            <Avatar
              src={
                userData.avatar
                  ? userData.avatar.startsWith('http')
                    ? userData.avatar
                    : `${serverUrl}${userData.avatar}`
                  : '/default_avatar.png'
              }
              alt={userData.username}
              sx={{
                width: 120,
                height: 120,
                mb: { xs: 2, md: 0 },
                mr: { md: 4 },
                alignSelf: { xs: 'center', md: 'flex-start' },
              }}
            />
            {/* Інформація */}
            <Box sx={{ flex: 1, minWidth: 0, maxWidth: 900 }}>
              <Typography variant="h5" gutterBottom>
                {userData.username}
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  {t('profile:level')}: {userData.level}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <GradientProgress
                    value={userData.experience % 100}
                    label={`${userData.experience % 100}/100 XP`}
                    size={240}
                    height={12}
                  />
                </Box>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
                  <Chip
                    icon={<SchoolIcon />}
                    label={`${t('profile:completed_courses')}: ${
                      userData.completedCoursesCount || 0
                    }`}
                    color="primary"
                    variant="outlined"
                  />
                  <Chip
                    icon={<QuizIcon />}
                    label={`${t('profile:completed_tests')}: ${
                      userData.completedTestsCount || 0
                    }`}
                    color="secondary"
                    variant="outlined"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Paper>

        {/* Вкладки с дополнительной информацией */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            borderRadius: '12px',
          }}
        >
          {/* Если admin/teacher — Tabs и все вкладки, иначе только достижения */}
          {currentUser.roles.includes('admin') || currentUser.roles.includes('teacher') ? (
            <>
              <Box
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: '16px',
                  p: '4px',
                  mb: 2,
                }}
              >
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label={t('profile:achievements')} />
                  <Tab label={t('profile:my_courses')} />
                  <Tab label={t('profile:test_results')} />
                </Tabs>
              </Box>
              <Box hidden={tabValue !== 0}>
                {!hasPrivateAccess ? (
                  <Box sx={{ textAlign: 'center', p: 4 }}>
                    <LockIcon
                      sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {t('profile:achievements_hidden')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t('profile:private_info')}
                    </Typography>
                  </Box>
                ) : loadingAchievements ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : achievements && achievements.length > 0 ? (
                  <Grid container spacing={3}>
                    {achievements.map((achievement) => (
                      <Grid item xs={12} sm={6} md={4} key={achievement._id}>
                        <AchievementCard achievement={achievement} />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ p: 2, textAlign: 'center' }}
                  >
                    {t('profile:no_achievements')}
                  </Typography>
                )}
              </Box>
              {/* Вкладка с курсами — только для admin/teacher */}
              <Box hidden={tabValue !== 1}>
                {!hasPrivateAccess ? (
                  <Box sx={{ textAlign: 'center', p: 4 }}>
                    <LockIcon
                      sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {t('profile:courses_hidden')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t('profile:private_info')}
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* 1. Курсы в прогрессе */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                      {t('profile:courses_in_progress')}
                    </Typography>
                    {loadingCourses ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : courses.inProgressCourses &&
                      courses.inProgressCourses.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 4 }}>
                        {courses.inProgressCourses.map((course) => (
                          <CourseCard
                            key={course._id}
                            course={course}
                            progress={
                              course.userProgress?.progress ||
                              course.progress ||
                              0
                            }
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ mb: 4 }}>
                        {t('profile:no_courses_in_progress')}
                      </Typography>
                    )}

                    {/* 2. Завершённые курсы */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                      {t('profile:completed_courses_title')}
                    </Typography>
                    {loadingCourses ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : courses.completedCourses &&
                      courses.completedCourses.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 4 }}>
                        {courses.completedCourses.map((course) => (
                          <CourseCard key={course._id} course={course} progress={100} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body1" sx={{ mb: 4 }}>
                        {t('profile:no_completed_courses')}
                      </Typography>
                    )}

                    {/* 3. Курсы, на которые записан (не начатые) */}
                    <Typography variant="h6" gutterBottom sx={{ mt: 4 }}>
                      {t('profile:enrolled_courses')}
                    </Typography>
                    {loadingCourses ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : courses.notStartedCourses &&
                      courses.notStartedCourses.length > 0 ? (
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center' }}>
                        {courses.notStartedCourses.map((course) => (
                          <CourseCard key={course._id} course={course} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body1">
                        {t('profile:no_enrolled_courses')}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
              {/* Вкладка с результатами тестов — только для admin/teacher */}
              <Box hidden={tabValue !== 2}>
                {!hasPrivateAccess ? (
                  <Box sx={{ textAlign: 'center', p: 4 }}>
                    <LockIcon
                      sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }}
                    />
                    <Typography variant="h6" gutterBottom>
                      {t('profile:test_results_hidden')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {t('profile:private_info')}
                    </Typography>
                  </Box>
                ) : loadingTestResults ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : testResults.results && testResults.results.length > 0 ? (
                  <Grid container spacing={2}>
                    {testResults.results.map((result) => (
                      <Grid item xs={12} md={6} key={result._id}>
                        <TestResultCard
                          result={result}
                          onRetake={async (testId) => {
                            try {
                              const testInfo = await testsAPI.getTestInfo(testId);
                              const { courseSlug, moduleSlug, lessonSlug } = testInfo.data;
                              const path = lessonSlug
                                ? `/courses/${courseSlug}/modules/${moduleSlug}/lessons/${lessonSlug}/tests/${testId}`
                                : `/courses/${courseSlug}/modules/${moduleSlug}/tests/${testId}`;
                              navigate(path);
                            } catch (error) {
                              toast.error(t('tests:errors.startTestFailed'));
                            }
                          }}
                          onViewDetails={(resultId) =>
                            navigate(`/test-results/${resultId}`)
                          }
                        />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography
                    variant="body1"
                    sx={{ p: 2, textAlign: 'center' }}
                  >
                    {t('profile:no_tests')}
                  </Typography>
                )}
              </Box>
            </>
          ) : (
            <Box>
              {/* Заголовок блока достижений показываем только если есть доступ */}
              {hasPrivateAccess && (
                <Typography variant="h6" gutterBottom>
                  {t('profile:achievements')}
                </Typography>
              )}
              {!hasPrivateAccess ? (
                <Box sx={{ textAlign: 'center', p: 4 }}>
                  <LockIcon
                    sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }}
                  />
                  <Typography variant="h6" gutterBottom>
                    {t('profile:achievements_hidden')}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {t('profile:private_info')}
                  </Typography>
                </Box>
              ) : loadingAchievements ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : achievements && achievements.length > 0 ? (
                <Grid container spacing={3}>
                  {achievements.map((achievement) => (
                    <Grid item xs={12} sm={6} md={4} key={achievement._id}>
                      <AchievementCard achievement={achievement} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
                  {t('profile:no_achievements')}
                </Typography>
              )}
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default UserProfile;
